import {
  Input,
  Component,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnDestroy,
} from '@angular/core';
import { DialogContent } from '@common/entities';
import { Utilities } from '@common/utils';
import { Observable, Subject } from 'rxjs';
import { GoabButtonSize, GoabButtonType, GoabButtonVariant } from '@abgov/ui-components-common';
@Component({
  standalone: false,
  selector: 'd3-modal-dialog',
  templateUrl: './modal-dialog.component.html',
  styleUrls: ['./modal-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class D3ModalDialogComponent implements OnDestroy {
  constructor(private cdr: ChangeDetectorRef) {}

  private _mutationObservers: MutationObserver[] = [];
  private _confirmHandled = false;

  ngOnDestroy(): void {
    this._mutationObservers.forEach((o) => o.disconnect());
    this._mutationObservers = [];
  }
  isOpen = false;
  confirm$ = new Subject<boolean>();
  @Input() uniqueId = 'id';
  // @Input() calloutVariant: 'information' | 'important' | 'success' | 'emergency' | 'event';

  //Modal trigger button/link properties
  @Input() linkText;
  @Input() buttonText;
  @Input() buttonType: GoabButtonType = 'primary';
  @Input() buttonVariant: GoabButtonVariant = undefined;
  @Input() buttonDisabled = false;
  @Input() dialogTriggerButtonWidth = 'auto';
  @Input() dialogTriggerButtonSize: GoabButtonSize | undefined = 'compact';

  @Input() closable = false; //Show close icon and allow clicking the background to close the modal.
  @Input() displayContent = false; //Will hide/show content inside of modal-dialog tags

  @Input() dialogContent: DialogContent;
  //dialogContent covers the properties below//
  /**
   * @deprecated Use `dialogContent` instead.
   */
  @Input() calloutVariant = null;
  /**
   * @deprecated Use `dialogContent` instead.
   */
  @Input() headerText;
  /**
   * @deprecated Use `dialogContent` instead.
   */
  @Input() bodyText;
  /**
   * @deprecated Use `dialogContent` instead.
   */
  @Input() cancelText = '';
  /**
   * @deprecated Use `dialogContent` instead.
   */
  @Input() confirmText = '';
  /**
   * @deprecated Use `dialogContent` instead.
   */
  @Input() okText = '';
  
  @Input() cancelButtonType: GoabButtonType = 'tertiary'; //allow override of button type for cancel. sometimes it'll be secondary or tertiary

  @Output() outputAction = new EventEmitter();
  @Output() closeAction = new EventEmitter();
  @Output() cancelAction = new EventEmitter();

  //use this to hide trigger buttons for option to manually trigger dialog with trigger button outside of this component.
  //see libs\aish-intake\ui\application\src\lib\pages\application.component.html
  @Input() hideUiElements = false;

  openModal(calloutVariant, event: Event = null, autoOpen = false): Observable<boolean> {
    //autoOpen are flagged true for dialogs that open without a button to trigger (automatically triggers by a non-click event)
    this.calloutVariant = calloutVariant;
    this.isOpen = true;
    this._confirmHandled = false;
    if (event) {
      event.preventDefault();
    }
    if (autoOpen) {
      //hack to remove container outline and focus from the content wrapper within dialog window
      //this only happens on dialogs that do not trigger off a click event (auto open).
      //seems to be a bug with design systems component, so this is a temporary hack until they figure this out
      this.removeContainerFocus();
    }

    this.cdr.markForCheck(); // Mark the component for change detection
    return this.confirm$.asObservable();
  }

  removeContainerFocus() {
    let handled = false;
    const containerWithBorder = '.modal-content';
    this.watchForElement(containerWithBorder, (modalContent) => {
      if (handled) {
        return;
      }
      handled = true;
      // console.log('Found:', modalContent);
      setTimeout(() => {
        modalContent.style.setProperty('outline', '0', 'important');
        // modalContent.blur();

        //now remove active focus on modal
        const activeElement = document.activeElement as any;
        if (activeElement) {
          activeElement.blur();
        }
      }, 0);
    });
  }

  closeModal() {
    this.isOpen = false;
    this.cdr.markForCheck(); // Mark the component for change detection
    this.closeAction.emit();
  }

  actionModal() {
    this.outputAction.emit();
    this.isOpen = false;
  }

  ok() {
    this.confirm$.next(true);
    this.closeModal();
  }

  confirm() {
    if (this._confirmHandled) {
      return;
    }
    this._confirmHandled = true;
    this.confirm$.next(true);
    this.actionModal();
  }

  cancel() {
    this.confirm$.next(false);
    this.cancelAction.emit();
    this.closeModal();
  }

  getText(text, fallbackText) {
    let returnValue = text;
    if (!Utilities.hasValue(text) && Utilities.hasValue(fallbackText)) {
      //if fallback has value then use that
      returnValue = fallbackText;
    }
    return returnValue;
  }

  //attribute variant cannot be empty string otherwise it throws a console error
  get getButtonVariant(): GoabButtonVariant | undefined {
    if (Utilities.hasValue(this.buttonVariant)) {
      return this.buttonVariant;
    }
    return undefined;
  }

  get getCalloutVariant() {
    if (Utilities.hasValue(this.calloutVariant)) {
      return this.calloutVariant;
    }
    return null;
  }

  //Hack fix to prevent outline on design component from showing up for dialogs that automatically pop up
  //Remove once (or if) design systems fixes the focus issue.
  watchForElement(selector: string, callback: (element: HTMLElement) => void) {
    const check = () => {
      const element = this.findShadowElementInContainer(this.uniqueId, selector);
      if (element) {
        callback(element);
        return true;
      }
      return false;
    };
    // Check immediately
    if (check()) {
      return;
    }
    const observeRoot = (root: Document | ShadowRoot) => {
      const observer = new MutationObserver(() => {
        if (check()) {
          observer.disconnect();
        }
        // Discover any new shadow roots
        root.querySelectorAll('*').forEach((el) => {
          const element = el as HTMLElement;

          if (element.shadowRoot) {
            observeRoot(element.shadowRoot);
          }
        });
      });
      this._mutationObservers.push(observer);
      observer.observe(root, {
        childList: true,
        subtree: true,
      });
      // Attach to existing shadow roots
      root.querySelectorAll('*').forEach((el) => {
        const element = el as HTMLElement;
        if (element.shadowRoot) {
          observeRoot(element.shadowRoot);
        }
      });
    };
    observeRoot(document);
  }

  findShadowElementInContainer(containerId: string, selector: string): HTMLElement | null {
    const search = (root: Document | ShadowRoot | HTMLElement): HTMLElement | null => {
      const found = root.querySelector(selector) as HTMLElement | null;
      if (found) {
        return found;
      }
      for (const el of Array.from(root.querySelectorAll('*'))) {
        const element = el as HTMLElement;
        if (element.shadowRoot) {
          const nestedFound = search(element.shadowRoot);

          if (nestedFound) {
            return nestedFound;
          }
        }
      }
      return null;
    };
    const container = document.getElementById(containerId + '-modal-dialog');
    if (!container) {
      return null;
    }
    return search(container);
  }
}
